import React from 'react';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
// @ts-ignore - ContextMenu is a JS component without types
import { ContextMenu, contextStyles } from '@birdi/context-menu/ContextMenu';
import MoreHorizontalSVG from '@birdi/icons/more-horizontal.svg';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  DragOverlay,
  useDndMonitor,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { LatestMaps } from './widgets/latest-maps';
import { LatestMapReports } from './widgets/latest-map-reports';
import { LatestAIDetections } from './widgets/latest-ai-detections';
import { MapCoverage } from './widgets/map-coverage';
import { Processing } from './widgets/processing';
import { TeamActivity } from './widgets/team-activity';
import { ActivityTrends } from './widgets/activity-trends';
import { RecentComments } from './widgets/recent-comments';
import { ProcessingUsage } from './widgets/processing-usage';
import { ProcessingReports } from './widgets/processing-reports';
import { ProcessingOverview } from './widgets/processing-overview';
import { MostViewedMaps } from './widgets/most-viewed-maps';
import { RecentlySharedMaps } from './widgets/recently-shared-maps';
import { RecentWorkspaces } from './widgets/recent-workspaces';
import { RecentlyInvitedUsers } from './widgets/recently-invited-users';
import { NumberWidgets } from './widgets/number-widgets';
import { GraphWidgets } from './widgets/graph-widgets';
import { GeneralWorkspaceInfo } from './general-workspace-info';
import { GeneralInfoGraphs } from './general-info-grapgs';
import { AddWidget } from './widgets/add-widget';
import { WIDGET_METADATA, WidgetSelectorModal } from './widgets/widget-selector-modal';
import ReorderingSVG from '@birdi/icons/reordering.svg';
import { useMetaSelector } from '../meta/reducer';
import { organisation as organisationApi, stroot } from '@birdi/js-sdk';
import { refreshIntercomAfterContentChange } from '@birdi/intercom';
import type { WorkspaceGeneralInfo } from '@birdi/js-sdk/src/public/organisation';

export type WidgetId =
  | 'latest-maps'
  | 'latest-map-reports'
  | 'latest-ai-detections'
  | 'map-coverage'
  | 'processing'
  | 'team-activity'
  | 'activity-trends'
  | 'recent-comments'
  | 'team-members'
  | 'guest-collaborators'
  | 'files-uploaded'
  | 'storage-used'
  | 'active-workspaces'
  | 'maps-created'
  | 'seats-usage'
  | 'guest-access-usage'
  | 'workspace-usage'
  | 'processing-usage'
  | 'processing-usage-graph'
  | 'storage-usage'
  | 'processing-reports'
  | 'processing-overview'
  | 'most-viewed-maps'
  | 'recently-shared-maps'
  | 'recent-workspaces'
  | 'recently-invited-users';

export type TabId = 'default' | 'processing' | 'insights' | 'usage' | 'collaboration';

/**
 * Narrowest a dashboard card column stays readable at, used as the flex basis
 * for the two-column card layout so it collapses on AVAILABLE width.
 *
 * It cannot be a media query. When the assistant panel is docked it takes 360px
 * out of the page (RootLayout pads the content), and a viewport media query
 * can't see that — so a 1440px laptop with the panel open has ~1080px of page
 * but still matched the ≥650px "two columns" rule, cramming two columns plus a
 * nav into the space of one. Two columns need 2 × this + the gap; under that
 * they wrap to one, which is what you want on a laptop with the panel open.
 */
const CARD_COLUMN_MIN = '360px';

export interface WidgetProps {
  isReorderMode?: boolean;
  isHovered?: boolean;
  isDragging?: boolean;
  /** True in drag overlay only — avoids duplicate Intercom anchor ids */
  suppressIntercomAnchor?: boolean;
}

export const WIDGET_REGISTRY: Record<WidgetId, React.ComponentType<WidgetProps>> = {
  'latest-maps': LatestMaps,
  'latest-map-reports': LatestMapReports,
  'latest-ai-detections': LatestAIDetections,
  'map-coverage': MapCoverage,
  'processing': Processing,
  'team-activity': TeamActivity,
  'activity-trends': ActivityTrends,
  'recent-comments': RecentComments,
  'processing-usage': ProcessingUsage,
  'processing-reports': ProcessingReports,
  'processing-overview': ProcessingOverview,
  'most-viewed-maps': MostViewedMaps,
  'recently-shared-maps': RecentlySharedMaps,
  'recent-workspaces': RecentWorkspaces,
  'recently-invited-users': RecentlyInvitedUsers,
  // Number widgets are handled separately by NumberWidgets component
  'team-members': () => null,
  'guest-collaborators': () => null,
  'files-uploaded': () => null,
  'storage-used': () => null,
  'active-workspaces': () => null,
  'maps-created': () => null,
  // Graph widgets are handled separately by GraphWidgets component
  'seats-usage': () => null,
  'guest-access-usage': () => null,
  'workspace-usage': () => null,
  'processing-usage-graph': () => null,
  'storage-usage': () => null,
};

export const TAB_WIDGETS: Record<TabId, WidgetId[]> = {
  'default': [],
  'processing': [
    'latest-maps',
    'processing-reports',
    'processing',
    'processing-overview',
    'processing-usage',
  ],
  'insights': [
    'latest-ai-detections',
    'latest-map-reports',
  ],
  'usage': [
    'recent-workspaces',
    'recently-invited-users',
    'activity-trends',
    'processing',
  ],
  'collaboration': [
    'recent-comments',
    'most-viewed-maps',
    'team-activity',
    'recently-shared-maps',
  ],
};

// SortableWidget component - makes widgets draggable when in reorder mode
// Defined outside TabContent to prevent remounting on reorder mode toggle
interface SortableWidgetProps {
  widgetId: WidgetId;
  isReorderMode: boolean;
}


const SortableWidget: React.FC<SortableWidgetProps> = React.memo(({ widgetId, isReorderMode }) => {
  const WidgetComponent = WIDGET_REGISTRY[widgetId];
  if (!WidgetComponent) {
    console.warn(`Widget ${widgetId} not found in registry`);
    return null;
  }

  const [isHovered, setIsHovered] = React.useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: widgetId,
    disabled: !isReorderMode, // Only draggable when in reorder mode
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition, // Disable transition while dragging for smoother feel
    opacity: isDragging ? 0.3 : 1, // Only apply opacity when dragging (full opacity otherwise)
    zIndex: isDragging ? 50 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      css={css`
        position: relative;
        cursor: ${isReorderMode ? 'grab' : 'default'};
        transition: opacity 0.2s ease, transform 0.2s ease;
        min-width: 0;
        max-width: 100%;
        ${isDragging && css`
          cursor: grabbing;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        `}
        &:active {
          cursor: ${isReorderMode ? 'grabbing' : 'default'};
        }
      `}
      {...attributes}
      {...(isReorderMode ? listeners : {})}
    >
      <div
        css={css`
          position: relative;
          ${isReorderMode && css`
            pointer-events: none;
          `}
        `}
      >
        <div
          css={css`
            ${isReorderMode && css`
              /* Apply opacity only to content (all children of widget root except the first one which is the header) */
              > * > *:not(:first-child) {
                opacity: 0.4;
              }
            `}
          `}
        >
          <WidgetComponent
            isReorderMode={isReorderMode}
            isHovered={isHovered}
            isDragging={isDragging}
          />
        </div>
      </div>
    </div>
  );
});

SortableWidget.displayName = 'SortableWidget';

export interface TabBarProps {
  activeTab: TabId;
  onTabChange: (tabId: TabId) => void;
  defaultTabWidgetsCount?: number;
  isReorderMode?: boolean;
  onStartReorder?: () => void;
  onDoneReorder?: () => void;
  onOpenAddWidgetModal?: () => void;
  canCustomizeDashboard?: boolean;
  /** Usage tab: org admins on main organisation only */
  showUsageTab?: boolean;
}

export const TabBar: React.FC<TabBarProps> = ({
  activeTab,
  onTabChange,
  defaultTabWidgetsCount = 0,
  isReorderMode = false,
  onStartReorder,
  onDoneReorder,
  onOpenAddWidgetModal,
  canCustomizeDashboard = false,
  showUsageTab = true,
}) => {
  const theme = useTheme();
  const isContextMenuClosingRef = React.useRef(false);

  const tabs: { id: TabId; label: string }[] = [
    { id: 'default', label: 'Default' },
    { id: 'processing', label: 'Processing' },
    { id: 'insights', label: 'Insights' },
    ...(showUsageTab ? [{ id: 'usage' as const, label: 'Usage' }] : []),
    { id: 'collaboration', label: 'Collaboration' },
  ];

  return (
    <div
      css={css`
        background: ${theme.mono0};
        width: 100%;
        display: flex;
        margin-bottom: 0.5em;
        border-top-left-radius: 1em;
        border-top-right-radius: 1em;
        overflow: visible;
        position: relative;
        z-index: 100;
        min-height: 3em;
        @media (max-width: ${breakpoints.xs}) {
          padding: 0 0.75em;
          width: 100%;
          max-width: 100%;
        }
      `}
    >
      {tabs.map((tab, index) => (
        <div
          key={tab.id}
          onClick={(e) => {
            // Only handle clicks if they're directly on the div (not on child buttons)
            if (e.target === e.currentTarget) {
              if (!isReorderMode && !isContextMenuClosingRef.current) {
                onTabChange(tab.id);
              }
            }
          }}
          css={css`
            background: ${activeTab === tab.id ? theme.lightOcean : theme.mono0};
            border: none;
            border-bottom: 3px solid ${activeTab === tab.id ? theme.deepOcean : 'transparent'};
            /* Flex to the CONTAINER (not viewport breakpoints) so the row
               adapts when the assistant panel reserves the right column. */
            flex: 1 1 0;
            min-width: 0;
            justify-content: center;
            padding: 0.75em 1em;
            font-size: 1em;
            color: ${activeTab === tab.id ? theme.deepOcean : theme.midnight};
            font-weight: ${activeTab === tab.id ? 'bold' : 'normal'};
            cursor: ${isReorderMode ? 'not-allowed' : 'pointer'};
            transition: background 0.2s, color 0.2s, border-color 0.2s;
            white-space: nowrap;
            margin-bottom: -1px;
            overflow: visible;
            position: relative;
            display: flex;
            align-items: center;
            gap: 0.5em;
            border-top-left-radius: ${index === 0 ? '1em' : 0};
            border-top-right-radius: ${index === tabs.length - 1 ? '1em' : 0};
            ${!isReorderMode && css`
              &:hover {
                color: ${activeTab === tab.id ? theme.deepOcean : theme.deepOcean};
              }
            `}
            @media (max-width: ${breakpoints.xs}) {
              padding: 0.75em 0.5em;
              font-size: 0.85em;
            }
          `}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation(); // Prevent event from bubbling to parent div
              // Prevent tab change if in reorder mode or if context menu is closing
              if (!isReorderMode && !isContextMenuClosingRef.current) {
                onTabChange(tab.id);
              }
            }}
            css={css`
              background: transparent;
              border: none;
              padding: 0;
              font-size: inherit;
              color: inherit;
              font-weight: inherit;
              cursor: ${isReorderMode ? 'not-allowed' : 'pointer'};
              flex: 0 1 auto;
              min-width: 0;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              text-align: center;
              &:focus {
                outline: none;
              }
            `}
          >
            {tab.label}
          </button>
          {tab.id === 'default' && canCustomizeDashboard && (
            <ContextMenu
              ButtonComponent={(props: any) => (
                <button
                  type="button"
                  disabled={activeTab !== 'default' || isReorderMode}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (activeTab === 'default' && !isReorderMode) {
                      props.onClick(e);
                    }
                  }}
                  aria-label="Customise"
                  css={css`
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0.5em;
                    background: transparent;
                    border: none;
                    cursor: ${activeTab === 'default' && !isReorderMode ? 'pointer' : 'not-allowed'};
                    border-radius: 6px;
                    color: ${activeTab === 'default' && !isReorderMode ? theme.midnight : theme.mono40};
                    opacity: ${activeTab === 'default' && !isReorderMode ? 1 : 0.5};
                    transition: background 0.15s ease;
                    &:hover {
                      background: ${activeTab === 'default' && !isReorderMode ? theme.mono20 : 'transparent'};
                    }
                    &:hover [data-customise-tooltip] {
                      opacity: 1;
                      visibility: visible;
                    }
                    &:disabled {
                      cursor: not-allowed;
                    }
                    &:focus {
                      outline: none;
                    }
                    svg {
                      width: 1.35em;
                      height: 1.35em;
                    }
                  `}
                  ref={props.toggleRef}
                >
                  <span
                    data-customise-tooltip
                    css={css`
                      position: absolute;
                      bottom: calc(100% + 2px);
                      left: 50%;
                      transform: translateX(-50%);
                      z-index: 10000;
                      background: white;
                      color: ${theme.midnight};
                      padding: 0.25rem 0.5rem;
                      border-radius: 4px;
                      font-size: 12px;
                      font-weight: 500;
                      line-height: 1.25;
                      white-space: nowrap;
                      box-shadow: ${theme.floatingShadow};
                      opacity: 0;
                      visibility: hidden;
                      transition: opacity 0.08s ease, visibility 0.08s ease;
                      pointer-events: none;
                    `}
                  >
                    Customise
                  </span>
                  <MoreHorizontalSVG />
                </button>
              )}
            >
              {(props: any) => (
                <div
                  css={css`
                    ${contextStyles(theme)};
                    // width: 10em;
                    z-index: 10000 !important;
                    position: relative;
                    border-radius: 5px;
                    button {
                      padding: 1em 1.25em;
                    }
                  `}
                >
                  <button
                    type="button"
                    disabled={isReorderMode}
                    onClick={() => {
                      if (!isReorderMode && onOpenAddWidgetModal) {
                        props.hide();
                        // Use setTimeout to ensure context menu closes before opening modal
                        setTimeout(() => {
                          onOpenAddWidgetModal();
                        }, 100);
                      }
                    }}
                    css={css`
                      &:disabled {
                        opacity: 0.5;
                        cursor: not-allowed;
                      }
                    `}
                  >
                    <span>Add report</span>
                  </button>
                  <button
                    type="button"
                    disabled={defaultTabWidgetsCount === 0 || isReorderMode}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent event from bubbling to tab button
                      if (defaultTabWidgetsCount > 0 && onStartReorder) {
                        isContextMenuClosingRef.current = true;
                        props.hide();
                        // Use setTimeout to ensure context menu closes before starting reorder
                        setTimeout(() => {
                          isContextMenuClosingRef.current = false;
                          onStartReorder();
                        }, 100); // Increased timeout to ensure context menu fully closes
                      }
                    }}
                    css={css`
                      &:disabled {
                        opacity: 0.5;
                        cursor: not-allowed;
                      }
                    `}
                  >
                    <span>Edit layout</span>
                  </button>
                </div>
              )}
            </ContextMenu>
          )}
        </div>
      ))}
    </div>
  );
};

export interface TabContentProps {
  activeTab: TabId;
  hasMapCommentsFeature: boolean;
  defaultTabWidgets?: WidgetId[];
  onDefaultTabWidgetsChange?: (widgetIds: WidgetId[]) => void;
  isLoadingWidgets?: boolean;
  isReorderMode?: boolean;
  onDoneReorder?: () => void;
  onOpenAddWidgetModalRef?: React.MutableRefObject<(() => void) | null>;
  canCustomizeDashboard?: boolean;
  showUsageTab?: boolean;
}

export const TabContent: React.FC<TabContentProps> = ({
  activeTab,
  hasMapCommentsFeature,
  defaultTabWidgets = [],
  onDefaultTabWidgetsChange,
  isLoadingWidgets = false,
  isReorderMode = false,
  onDoneReorder,
  onOpenAddWidgetModalRef,
  canCustomizeDashboard = false,
  showUsageTab = true,
}) => {
  const theme = useTheme();
  const meta = useMetaSelector();
  const workspaceId = meta.workspace?.id ?? meta.organisation?.id ?? null;
  const restrictMainOrgWidgets = Boolean(meta.workspace);
  const [workspaceGeneralInfo, setWorkspaceGeneralInfo] =
    React.useState<WorkspaceGeneralInfo | null>(null);
  const [workspaceGeneralInfoLoading, setWorkspaceGeneralInfoLoading] =
    React.useState(false);
  const [isAddWidgetModalOpen, setIsAddWidgetModalOpen] = React.useState(false);

  const isCardWidget = React.useCallback((widgetId: WidgetId): boolean => {
    const widgetMeta = WIDGET_METADATA.find((m) => m.id === widgetId);
    return widgetMeta?.category === 'cards';
  }, []);
  const isGraphWidget = React.useCallback((widgetId: WidgetId): boolean => {
    const widgetMeta = WIDGET_METADATA.find((m) => m.id === widgetId);
    return widgetMeta?.category === 'graphs';
  }, []);

  const hasNumberWidgets = defaultTabWidgets.some(
    (id) => !isCardWidget(id) && !isGraphWidget(id)
  );
  const needsWorkspaceGeneralInfo =
    (activeTab === 'usage' && showUsageTab) ||
    (activeTab === 'default' && hasNumberWidgets);

  React.useEffect(() => {
    if (!needsWorkspaceGeneralInfo || !workspaceId) return;
    let cancelled = false;
    setWorkspaceGeneralInfo(null);
    setWorkspaceGeneralInfoLoading(true);
    organisationApi
      .getWorkspaceGeneralInfo(stroot('weeh5y'))
      .then((res) => {
        if (!cancelled && res.body) {
          setWorkspaceGeneralInfo(res.body);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Error fetching workspace general info:', err);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setWorkspaceGeneralInfoLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [needsWorkspaceGeneralInfo, workspaceId]);

  /*
   * Intercom contextual hints (info icons, tooltips, etc.) target DOM nodes by selector, e.g. #latest-maps.
   * On a SPA they often run before those nodes exist: (1) Default tab shows "Loading…" until dashboard
   * widgets are fetched, then only selected widgets render. (2) Non-default tabs unmount entirely while
   * inactive, so their widget ids are absent until the user switches tab.
   *
   * A bare Intercom('update') is not enough — see @birdi/intercom refreshIntercomAfterContentChange():
   * reattach_activator + update(intercomSettings), matching Intercom’s recommended SPA install pattern.
   *
   * We re-run after: default tab finished loading, widget list changes, or tab switch (deps below).
   * Double requestAnimationFrame plus delayed timeouts catches widgets that paint after the first frame.
   */
  React.useEffect(() => {
    if (activeTab === 'default' && isLoadingWidgets) {
      return;
    }
    let cancelled = false;
    let raf2 = 0;
    const run = () => {
      if (!cancelled) {
        refreshIntercomAfterContentChange();
      }
    };
    const id1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(run);
    });
    const t1 = window.setTimeout(run, 400);
    const t2 = window.setTimeout(run, 1200);
    return () => {
      cancelled = true;
      cancelAnimationFrame(id1);
      if (raf2) {
        cancelAnimationFrame(raf2);
      }
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [activeTab, isLoadingWidgets, defaultTabWidgets]);

  const [draftWidgets, setDraftWidgets] = React.useState<WidgetId[]>(defaultTabWidgets);
  // Track widget order during reorder mode (separate from committed state)
  const [reorderWidgets, setReorderWidgets] = React.useState<WidgetId[]>(defaultTabWidgets);
  // Track active dragged widget for DragOverlay
  const [activeId, setActiveId] = React.useState<WidgetId | null>(null);

  // When modal opens, copy committed state to draft
  const handleOpenModal = React.useCallback(() => {
    setDraftWidgets(defaultTabWidgets);
    setIsAddWidgetModalOpen(true);
  }, [defaultTabWidgets]);

  // Expose handleOpenModal via ref so TabBar can call it
  React.useEffect(() => {
    if (onOpenAddWidgetModalRef) {
      onOpenAddWidgetModalRef.current = handleOpenModal;
    }
    return () => {
      if (onOpenAddWidgetModalRef) {
        onOpenAddWidgetModalRef.current = null;
      }
    };
  }, [handleOpenModal, onOpenAddWidgetModalRef]);

  // When modal closes, commit draft to committed state
  const handleCloseModal = React.useCallback(() => {
    if (onDefaultTabWidgetsChange) {
      onDefaultTabWidgetsChange(draftWidgets);
    }
    setIsAddWidgetModalOpen(false);
  }, [draftWidgets, onDefaultTabWidgetsChange]);

  // Handle modal open change (for close button, ESC, etc.)
  const handleModalOpenChange = React.useCallback((open: boolean) => {
    if (!open) {
      handleCloseModal();
    } else {
      handleOpenModal();
    }
  }, [handleOpenModal, handleCloseModal]);

  // Update draft widgets while modal is open
  const handleDraftChange = React.useCallback((widgetIds: WidgetId[]) => {
    setDraftWidgets(widgetIds);
  }, []);

  // Sync draft with committed state when it changes externally (but not while modal is open)
  React.useEffect(() => {
    if (!isAddWidgetModalOpen) {
      setDraftWidgets(defaultTabWidgets);
    }
  }, [defaultTabWidgets, isAddWidgetModalOpen]);

  // Sync reorderWidgets with defaultTabWidgets when entering reorder mode
  // Only include card widgets in reorderWidgets since numbers and graphs are not reorderable
  React.useEffect(() => {
    if (isReorderMode) {
      // Only sync card widgets when entering reorder mode
      const cardWidgetsOnly = defaultTabWidgets.filter(isCardWidget);
      setReorderWidgets(cardWidgetsOnly);
    }
  }, [isReorderMode, defaultTabWidgets]);

  // Register getter function for reorderWidgets so TabBar can access it
  // Use a ref to ensure we always get the latest reorderWidgets value
  const reorderWidgetsRef = React.useRef(reorderWidgets);
  React.useEffect(() => {
    reorderWidgetsRef.current = reorderWidgets;
  }, [reorderWidgets]);

  // Handle done reorder - save current reorderWidgets state when done button is clicked
  React.useEffect(() => {
    if (!onDoneReorder) return;

    // Create a handler that saves the current reorderWidgets when called
    // Merge reordered card widgets with numbers and graphs from defaultTabWidgets
    const handleDone = () => {
      if (onDefaultTabWidgetsChange && isReorderMode && activeTab === 'default') {
        // Get numbers and graphs from defaultTabWidgets (they don't change during reorder)
        const numbersAndGraphs = defaultTabWidgets.filter(
          (id) => !isCardWidget(id)
        );
        // Merge: reordered card widgets + unchanged numbers and graphs
        const mergedWidgets = [...reorderWidgetsRef.current, ...numbersAndGraphs];
        onDefaultTabWidgetsChange(mergedWidgets);
      }
      if (onDoneReorder) {
        onDoneReorder();
      }
    };

    // Store the handler globally so TabBar can access it
    if (isReorderMode && activeTab === 'default') {
      (window as any).__dashboardDoneHandler = handleDone;
    } else {
      delete (window as any).__dashboardDoneHandler;
    }

    return () => {
      delete (window as any).__dashboardDoneHandler;
    };
  }, [onDoneReorder, onDefaultTabWidgetsChange, isReorderMode, activeTab]);

  // Set up drag-and-drop sensors (must be called unconditionally - Rules of Hooks)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag start - track active widget for DragOverlay
  const handleDragStart = React.useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as WidgetId);
  }, []);

  // Handle drag over - update visual feedback during drag
  // This provides real-time visual feedback as items move to make space
  const handleDragOver = React.useCallback((event: DragOverEvent) => {
    const { active, over, delta } = event;

    // Only handle if in reorder mode
    if (!isReorderMode || !over || active.id === over.id) {
      return;
    }

    // Only allow dragging card widgets
    const activeWidgetId = active.id as WidgetId;
    const overWidgetId = over.id as WidgetId;
    if (!isCardWidget(activeWidgetId) || !isCardWidget(overWidgetId)) {
      return;
    }

    // Get current widget order from reorderWidgets (which only contains card widgets)
    const currentOrder = [...reorderWidgets];

    // Find the indices of the dragged widget and the target position
    const oldIndex = currentOrder.indexOf(activeWidgetId);
    const newIndex = currentOrder.indexOf(overWidgetId);

    // If either widget not found, do nothing
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Determine which column each widget is in (even index = left, odd index = right)
    const oldColumn = oldIndex % 2 === 0 ? 'left' : 'right';
    const newColumn = newIndex % 2 === 0 ? 'left' : 'right';

    // Check if dragging to same column or different column
    const isSameColumn = oldColumn === newColumn;

    if (isSameColumn) {
      // Same column: Work with the column's array directly to keep items in the same column
      // Extract the column array (left = even indices, right = odd indices)
      const columnArray = oldColumn === 'left'
        ? currentOrder.filter((_, index) => index % 2 === 0)
        : currentOrder.filter((_, index) => index % 2 === 1);

      // Find positions within the column array
      const columnOldIndex = Math.floor(oldIndex / 2);
      const columnNewIndex = Math.floor(newIndex / 2);

      // Use arrayMove on the column array to shift items correctly
      // - If dragging down (columnOldIndex < columnNewIndex): item moves down, middle items shift up
      // - If dragging up (columnOldIndex > columnNewIndex): item moves up, middle items shift down
      const reorderedColumn = arrayMove(columnArray, columnOldIndex, columnNewIndex);

      // Reconstruct the flat interleaved array with the updated column
      const newOrder = [...currentOrder];
      if (oldColumn === 'left') {
        // Update even indices with reordered left column
        reorderedColumn.forEach((widgetId, colIndex) => {
          newOrder[colIndex * 2] = widgetId;
        });
      } else {
        // Update odd indices with reordered right column
        reorderedColumn.forEach((widgetId, colIndex) => {
          newOrder[colIndex * 2 + 1] = widgetId;
        });
      }

      setReorderWidgets(newOrder);
    } else {
      // Cross column: Show swap visualization by swapping positions
      // This gives a clear indication that items will swap without moving other items
      const newOrder = [...currentOrder];
      newOrder[oldIndex] = currentOrder[newIndex];
      newOrder[newIndex] = currentOrder[oldIndex];
      setReorderWidgets(newOrder);
    }
  }, [isReorderMode, reorderWidgets]);

  // Handle drag end - always swap the two widgets for consistency
  // Simple logic: Swap the dragged widget with the widget at the target position
  // This works for both same-column and cross-column drags
  const handleDragEnd = React.useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    // Clear active widget
    setActiveId(null);

    // If no over target or dragging to same position, do nothing
    if (!over || active.id === over.id) {
      return;
    }

    // Only handle if in reorder mode
    if (!isReorderMode) {
      return;
    }

    // Only allow dragging card widgets
    const activeWidgetId = active.id as WidgetId;
    const overWidgetId = over.id as WidgetId;
    if (!isCardWidget(activeWidgetId) || !isCardWidget(overWidgetId)) {
      return;
    }

    // Get current widget order from reorderWidgets (which only contains card widgets)
    const currentOrder = [...reorderWidgets];

    // Find the indices of the dragged widget and the target position
    const oldIndex = currentOrder.indexOf(activeWidgetId);
    const newIndex = currentOrder.indexOf(overWidgetId);

    // If either widget not found, do nothing
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Always swap: Simple and consistent for both same-column and cross-column
    const newOrder = [...currentOrder];
    newOrder[oldIndex] = currentOrder[newIndex];
    newOrder[newIndex] = currentOrder[oldIndex];

    setReorderWidgets(newOrder);
  }, [isReorderMode, reorderWidgets]);

  const renderWidget = (widgetId: WidgetId) => {
    // Always use SortableWidget to prevent remounting when toggling reorder mode
    // It's disabled when not in reorder mode, so it behaves like a regular widget
    return <SortableWidget key={widgetId} widgetId={widgetId} isReorderMode={isReorderMode} />;
  };

  const renderWidgets = (widgetIds: WidgetId[]) => {
    // Filter out recent-comments if feature flag is not enabled
    const filteredWidgetIds = widgetIds.filter(
      (id) => id !== 'recent-comments' || hasMapCommentsFeature
    );
    return filteredWidgetIds.map(renderWidget).filter(Boolean);
  };

  // Special handling for default tab
  if (activeTab === 'default') {
    // Show loading state while fetching widgets
    if (isLoadingWidgets) {
      return (
        <div
          css={css`
            padding: 2em;
            text-align: center;
            color: ${theme.mono60};
          `}
        >
          Loading dashboard...
        </div>
      );
    }

    // Render "Save order" button in fixed position when in reorder mode
    const SaveOrderButton = isReorderMode ? (
      <button
        onClick={() => {
          // Call the handler registered by TabContent
          const handler = (window as any).__dashboardDoneHandler;
          if (handler && typeof handler === 'function') {
            handler();
          } else if (onDoneReorder) {
            // Fallback to just exiting reorder mode
            onDoneReorder();
          }
        }}
        type="button"
        css={css`
          position: fixed;
          right: 3em;
          bottom: 3em;
          background: ${theme.deepOcean};
          color: ${theme.mono0};
          border: none;
          border-radius: 15px;
          padding: 1.25em 1.5em;
          font-size: 1.1em;
          font-weight: 700;
          cursor: pointer;
          z-index: 10000;
          box-shadow: 0 4px 12px rgba(0, 120, 192, 0.4);
          transition: all 0.2s;
          white-space: nowrap;
          visibility: visible;
          opacity: 1;
          &:hover:not(:disabled) {
            box-shadow: 0 6px 16px rgba(0, 120, 192, 0.5);
            transform: scale(1.05);
          }
          &:active:not(:disabled) {
            transform: scale(0.98);
          }
          &:disabled {
            opacity: 0.8;
            cursor: not-allowed;
            pointer-events: auto;
          }
          @media (max-width: ${breakpoints.xs}) {
            right: 1em;
            bottom: 1em;
            padding: 0.6em 1.2em;
            font-size: 0.9em;
          }
        `}
      >
        Save order
      </button>
    ) : null;

    // Use draftWidgets when modal is open, otherwise use defaultTabWidgets
    // For reorder mode, we still use defaultTabWidgets for the full list (including numbers/graphs)
    // reorderWidgets only tracks card widget order for drag-and-drop
    // This prevents remounting AddWidget while modal is open and keeps numbers/graphs visible
    const widgetIds = isAddWidgetModalOpen
      ? draftWidgets
      : defaultTabWidgets;

    // Separate cards (reorderable) from numbers and graphs (not reorderable)
    // In reorder mode, use reorderWidgets for cards (maintains drag state), otherwise use widgetIds
    const cardWidgets = isReorderMode
      ? reorderWidgets
      : widgetIds.filter(isCardWidget);
    const numberWidgets = widgetIds.filter((id) => !isCardWidget(id) && !isGraphWidget(id));
    const graphWidgets = widgetIds.filter(isGraphWidget);

    // Split card widgets into two columns
    const leftColumn = cardWidgets.filter((_, index) => index % 2 === 0);
    const rightColumn = cardWidgets.filter((_, index) => index % 2 === 1);

    return (
      <>
        {SaveOrderButton}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
        {/* Single SortableContext for card widgets only (numbers and graphs are not draggable) */}
        <SortableContext
          items={isReorderMode ? reorderWidgets : []}
          strategy={verticalListSortingStrategy}
        >
          {/* Numbers Grid - rendered first, like usage tab */}
          {numberWidgets.length > 0 && (
            <NumberWidgets
              selectedWidgetIds={numberWidgets}
              workspaceGeneralInfo={workspaceGeneralInfo}
              workspaceGeneralInfoLoading={workspaceGeneralInfoLoading}
            />
          )}

          {/* Graphs Grid - rendered after numbers, like usage tab */}
          {graphWidgets.length > 0 && (
            <GraphWidgets selectedWidgetIds={graphWidgets} />
          )}

          {/*
            Cards in two columns, collapsing to one on AVAILABLE width rather
            than viewport width. This has to be intrinsic (wrap + a flex basis)
            instead of a media query: when the assistant panel is docked it
            takes 360px out of the page, which a viewport media query cannot
            see — so on a laptop the dashboard kept rendering two columns into
            the space of one and everything inside them wrapped to pieces.
            CARD_COLUMN_MIN is the narrowest a card column stays readable at.
          */}
          <div
            css={css`
              display: flex;
              flex-wrap: wrap;
              gap: 0.5em;
              padding: 0;
              align-items: flex-start;
              min-width: 0;
              width: 100%;
            `}
          >
            {/* Left Column */}
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 0.5em;
                flex: 1 1 ${CARD_COLUMN_MIN};
                min-width: 0;
              `}
            >
              {renderWidgets(leftColumn)}
              {canCustomizeDashboard && (
                <div
                  css={css`
                    @media (max-width: ${breakpoints.xs}) {
                      display: none;
                    }
                  `}
                >
                  <AddWidget
                    onOpenModal={handleOpenModal}
                    isReorderMode={isReorderMode}
                  />
                </div>
              )}
            </div>
            {/* Right Column */}
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 0.5em;
                flex: 1 1 ${CARD_COLUMN_MIN};
                min-width: 0;
              `}
            >
              {renderWidgets(rightColumn)}
              {canCustomizeDashboard && (
                  <AddWidget
                    onOpenModal={handleOpenModal}
                    isReorderMode={isReorderMode}
                  />
              )}
            </div>
          </div>
        </SortableContext>
        {/* Single WidgetSelectorModal instance shared by both AddWidget components */}
        {canCustomizeDashboard && (
          <WidgetSelectorModal
            open={isAddWidgetModalOpen}
            onOpenChange={handleModalOpenChange}
            onWidgetsChange={handleDraftChange}
            selectedWidgetIds={draftWidgets}
            restrictMainOrgWidgets={restrictMainOrgWidgets}
          />
        )}
        {/* DragOverlay for better visual feedback during drag */}
        <DragOverlay>
          {activeId ? (() => {
            const WidgetComponent = WIDGET_REGISTRY[activeId];
            if (!WidgetComponent) return null;
            return (
              <div
                css={css`
                  opacity: 0.95;
                  // transform: rotate(2deg);
                  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
                  cursor: grabbing;
                  border-radius: 0.5em;
                  overflow: hidden;
                `}
              >
                <WidgetComponent
                  isReorderMode={true}
                  isHovered={false}
                  isDragging={true}
                  suppressIntercomAnchor
                />
              </div>
            );
          })() : null}
        </DragOverlay>
      </DndContext>
      </>
    );
  }

  if (activeTab === 'usage' && !showUsageTab) {
    return null;
  }

  const widgetIds = TAB_WIDGETS[activeTab];
  const showGeneralWorkspaceInfo = activeTab === 'usage' && showUsageTab;

  if (widgetIds.length === 0 && !showGeneralWorkspaceInfo) {
    return (
      <div
        css={css`
          padding: 2em;
          text-align: center;
          color: ${theme.mono60};
        `}
      >
        No widgets configured for this tab.
      </div>
    );
  }

  // Split widgets into two columns
  const leftColumn = widgetIds.filter((_, index) => index % 2 === 0);
  const rightColumn = widgetIds.filter((_, index) => index % 2 === 1);

  return (
    <>
      {showGeneralWorkspaceInfo && (
        <>
          <div
            css={css`
              padding: 0;
              margin-bottom: 0.5em;
            `}
          >
            <GeneralWorkspaceInfo
              data={workspaceGeneralInfo}
              isLoading={workspaceGeneralInfoLoading}
            />
          </div>
          <div
            css={css`
              padding: 0;
              margin-bottom: 0.5em;
            `}
          >
            <GeneralInfoGraphs />
          </div>
        </>
      )}
      {/* Same available-width collapse as the default tab — see CARD_COLUMN_MIN. */}
      <div
        css={css`
          display: flex;
          flex-wrap: wrap;
          gap: 0.5em;
          padding: 0;
          align-items: flex-start;
          min-width: 0;
          width: 100%;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 0.5em;
            flex: 1 1 ${CARD_COLUMN_MIN};
            min-width: 0;
          `}
        >
          {renderWidgets(leftColumn)}
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 0.5em;
            flex: 1 1 ${CARD_COLUMN_MIN};
            min-width: 0;
          `}
        >
          {renderWidgets(rightColumn)}
        </div>
      </div>
    </>
  );
};

